import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const usePendingFriendRequests = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: pendingCount = 0, isLoading } = useQuery({
    queryKey: ["pending-friend-requests", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("user_connections")
        .select("*", { count: "exact", head: true })
        .eq("connected_user_id", user.id)
        .eq("status", "pending");

      if (error) {
        console.error("Error fetching pending friend requests:", error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 30000, // 30 seconds
  });

  // Subscribe to real-time changes on user_connections
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("pending-friend-requests")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_connections",
          filter: `connected_user_id=eq.${user.id}`,
        },
        () => {
          // Invalidate and refetch the query when changes occur
          queryClient.invalidateQueries({
            queryKey: ["pending-friend-requests", user.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return {
    pendingCount,
    isLoading,
  };
};
